import React, { useEffect, useRef, useState } from "react";
import { GridCellProps, GridRowProps } from "@progress/kendo-react-grid";
import styles from "styles.module.css";
import { COLOR } from "utils/constant";
import {
	deleteRegistrationApi,
	getCatchRegistrationLineApi,
	getCatchRegistrationListApi,
	RegistrationListParams,
} from "api/registration";
import {
	gridColumnItemsHandler,
	lastColumItems,
	registrationDetailGridItems,
} from "./utils/GridColumns";
import Filter from "./utils/Filter";
import { useNavigate, useLocation } from "react-router";
import { PageTypePath } from "utils/pages";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";

import "./styles.css";
import "../landingNote/styles.css";
import { addMonths, addWeeks } from "date-fns";
import OverLayLoading, { Loading } from "ui/Loading";
import GridCustomPage from "ui/grid/GridPage";
import { useTranslation } from "react-i18next";
import CustomDetailedPage from "ui/grid/DetailPage";
import { PageAccessByMenu } from "interface/user";
import { useMenuItemStore } from "store/menuItem";
import { findMenuItemByPath } from "utils/helper";
import { getPageAccessApi } from "api/menu";
import { Registration } from "interface/registration";
import RegistrationMobileList from "./RegistrationMobileList";
import CustomModal from "ui/modal/Modal";
import { useUserStore } from "store";

const pageSize = 60;

const RegistrationList: React.FC<{}> = ({}) => {
	const [pageAccess, setPageAccess] = useState<PageAccessByMenu | null>(null);
	const { t, i18n } = useTranslation();
	const { isAdmin } = useUserStore();

	const router = useNavigate();
	const location = useLocation();
	const { menuItems } = useMenuItemStore();

	const currentPath = location.pathname;

	const overlayRef = useRef<HTMLDivElement>(null);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1600);
	const [mobileData, setMobileData] = useState<Registration[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);
	const mobileRequestInProgress = useRef(false);
	const mobileDataFinished = useRef(false);
	const mobileNextSkip = useRef(0);
	const [showDeleteMobile, setShowDeleteMobile] = useState(false);
	const [mobileDeletingItem, setMobileDeletingItem] =
		useState<Registration | null>(null);

	const [filterOptions, setFilterOptions] = useState<RegistrationListParams>({
		take: pageSize,
		skip: 0,
		fromDate: isAdmin ? addWeeks(new Date(), -26) : addMonths(new Date(), -6),
		toDate: new Date(),
	});

	useEffect(() => {
		const pageHandler = async () => {
			const menu = findMenuItemByPath(menuItems, currentPath);
			try {
				if (!pageAccess && menu) {
					const access = await getPageAccessApi(menu.id);
					setPageAccess(access);
				}
			} catch (error) {
				console.error("Error fetching page access:", error);
			} finally {
				// setPageAccessLoading(false);
			}
		};
		pageHandler();
	}, [currentPath, menuItems]);

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 1600);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	const navigate = useNavigate();
	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const requestMobileData = (skip: number, take: number, resetData = false) => {
		if (mobileRequestInProgress.current || mobileDataFinished.current) {
			return;
		}

		mobileRequestInProgress.current = true;
		setMobileLoading(true);

		getCatchRegistrationListApi({
			...filterOptions,
			skip,
			take,
		})
			.then((response) => {
				const incoming = (response?.data || []) as Registration[];

				if (incoming.length === 0) {
					mobileDataFinished.current = true;
					if (resetData) {
						setMobileData([]);
					}
					return;
				}

				mobileNextSkip.current = skip + take;

				if (resetData) {
					setMobileData(incoming);
				} else {
					setMobileData((prev) => [...prev, ...incoming]);
				}

				const loadedCount = skip + incoming.length;
				if (loadedCount >= (response?.totalCount || 0)) {
					mobileDataFinished.current = true;
				}
			})
			.finally(() => {
				mobileRequestInProgress.current = false;
				setMobileLoading(false);
			});
	};

	const refreshMobileData = () => {
		mobileDataFinished.current = false;
		mobileNextSkip.current = 0;
		setMobileData([]);
		requestMobileData(0, pageSize, true);
	};

	useEffect(() => {
		if (!isMobileView) {
			return;
		}
		refreshMobileData();
	}, [isMobileView, filterOptions]);

	const editItem = (row: GridCellProps) => {
		let step = 0;

		if (row.dataItem.noteType === 0 && row.dataItem.numberOfLines > 0) {
			step = 1;
		}
		router(
			//`/${PageTypePath.RegistrationForm}/${row.dataItem.registrationId}/${step}`,
			`/${PageTypePath.RegistrationForm}/${row.dataItem.registrationId}/${row.dataItem.currentStep}`,
			{
				state: {
					id: row.dataItem.registrationId,
					step: step,
				},
			},
		);
	};

	const editItemByRegistration = (item: Registration) => {
		let step = 0;

		if (item.noteType === 0 && item.numberOfLines > 0) {
			step = 1;
		}

		router(`/${PageTypePath.RegistrationForm}/${item.registrationId}/${step}`, {
			state: {
				id: item.registrationId,
				step,
			},
		});
	};

	const startDeleteMobileRegistration = (item: Registration) => {
		setMobileDeletingItem(item);
		setShowDeleteMobile(true);
	};

	const confirmDeleteMobileRegistration = () => {
		if (!mobileDeletingItem) {
			setShowDeleteMobile(false);
			return;
		}

		deleteRegistrationApi(mobileDeletingItem.registrationId)
			.then(() => {
				setShowDeleteMobile(false);
				setMobileDeletingItem(null);
				refreshMobileData();
			})
			.catch(() => {
				setShowDeleteMobile(false);
			});
	};

	if (!pageAccess) {
		return <Loading show={true} />;
	}
	return (
		<div className={styles.PageContainer}>
			<div ref={overlayRef} style={{ display: "none" }}>
				<OverLayLoading />
			</div>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("RegistrationList")}
				</legend>
			</div>

			<div>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					onNewRegistration={() =>
						navigate(`/${PageTypePath.RegistrationForm}`)
					}
					canCreate={pageAccess?.create === true}
					pageSize={pageSize}
					isMobileView={isMobileView}
				/>
			</div>
			<div style={{ marginTop: 30 }}>
				{isMobileView ? (
					<RegistrationMobileList
						items={mobileData}
						loading={mobileLoading}
						onEdit={editItemByRegistration}
						onDelete={startDeleteMobileRegistration}
						canDelete={pageAccess?.delete === true}
						onLoadMore={() =>
							requestMobileData(mobileNextSkip.current, pageSize)
						}
					/>
				) : (
					<GridCustomPage
						dataKey="registrationId"
						gridColumns={gridColumnItemsHandler()}
						// detailPage={DetailedPage}
						detailPage={(prop) => (
							<CustomDetailedPage
								grids={registrationDetailGridItems}
								dataApi={() =>
									getCatchRegistrationLineApi(
										prop.dataItem.registrationId,
										prop.dataItem.status,
									)
								}
							/>
						)}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						apiMethod={getCatchRegistrationListApi}
						filter={filterOptions}
						lastColWidth="175px"
						itemAboveHeight={100}
						pageAccess={pageAccess}
						handelShowRowTextColor={(rowData: GridRowProps) => {
							if (rowData.dataItem.status === 0) {
								return COLOR.Grey20;
							}
							return COLOR.TextLight;
						}}
						deleteItem={{
							api: deleteRegistrationApi,
							info: {
								title: t("DeleteRegistration"),
								description: t("DeleteRegistrationConfirmation"),
							},
							showConditionValue: 0,
							conditionField: "status",
						}}
						lastColBtns={lastColumItems({
							edit: editItem,
						})}
					/>
				)}
				{showDeleteMobile && (
					<CustomModal
						title={t("DeleteRegistration")}
						onClose={() => {
							setShowDeleteMobile(false);
							setMobileDeletingItem(null);
						}}
						actions={[
							{
								label: t("Cancel"),
								onClick: () => {
									setShowDeleteMobile(false);
									setMobileDeletingItem(null);
								},
								bgColor: COLOR.Green,
								color: "white",
							},
							{
								label: t("Yes"),
								onClick: confirmDeleteMobileRegistration,
							},
						]}
					>
						<p style={{ padding: "16px 0" }}>
							{t("DeleteRegistrationConfirmation")}
						</p>
					</CustomModal>
				)}
			</div>
		</div>
	);
};

export default RegistrationList;
